import { noop } from "lodash";
import { TEMPLATE_KEYS } from "PFApp/constants/templates";
import React, { createContext, useCallback, useContext, useState } from "react";

import { Row } from "../../types";

export const activityTypeToSidePanelTypeMap = {
  [TEMPLATE_KEYS.ROLE]: "role",
  [TEMPLATE_KEYS.AUDIT_ROLE]: "role",
  [TEMPLATE_KEYS.ENGAGEMENT]: "engagement",
  [TEMPLATE_KEYS.AUDIT_ENGAGEMENT]: "engagement"
} as const;

export type DetailsPanelData = {
  id: number;
  type: "booking" | "role" | "engagement" | "profile";
  openAllocateManually?: boolean;
  isEditPermitted?: boolean;
  roleBookingsIds?: number[];
} & Partial<Pick<Row, "original">>;

type DetailsPanelContext = {
  detailsData?: DetailsPanelData;
  openDetailsPanel: (data: DetailsPanelData, stack?: boolean) => void;
  closeDetailsPanel: () => void;
};

const DetailsPanelContext = createContext({
  openDetailsPanel: noop,
  closeDetailsPanel: noop
} as DetailsPanelContext);

export const DetailsPanelContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [detailsData, setDetailsData] = useState<DetailsPanelData>();
  const [detailsDataStack, setDetailsDataStack] = useState<DetailsPanelData[]>([]);

  const setDetailsPanelData = useCallback(
    (data: DetailsPanelData, stack = false) => {
      if (stack && detailsData) {
        setDetailsDataStack([...detailsDataStack, detailsData]);
      } else if (!stack) {
        setDetailsDataStack([]);
      }

      setDetailsData(data);
    },
    [detailsData, detailsDataStack]
  );

  const popDetailsPanelData = useCallback(() => {
    const stack = [...detailsDataStack];
    const detailsPanelDataToSet = stack.pop();
    setDetailsDataStack(stack);
    setDetailsData(detailsPanelDataToSet);
  }, [detailsDataStack]);

  const value = {
    detailsData,
    openDetailsPanel: setDetailsPanelData,
    closeDetailsPanel: popDetailsPanelData
  };

  return <DetailsPanelContext.Provider value={value}>{children}</DetailsPanelContext.Provider>;
};

export const useDetailsPanelContext = () => useContext(DetailsPanelContext);
